import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import { ChromePicker } from 'react-color';

import useClickOutside from '../../../../../../../../hooks/useClickOutside';
import Label from '../../../../../../../../components/global/Label';

const propTypes = {
  visible: PropTypes.bool.isRequired,
  tickerTape: PropTypes.shape({
    text: PropTypes.string,
    color: PropTypes.string,
    backgroundColor: PropTypes.string,
  }),
  tickerTapeEnabled: PropTypes.bool.isRequired,
  onUpdateTickerTape: PropTypes.func.isRequired,
  onToggleTickerTapeEnabled: PropTypes.func.isRequired,
};

const SecondaryTickerTapeView = ({
  visible,
  tickerTape,
  tickerTapeEnabled,
  onUpdateTickerTape,
  onToggleTickerTapeEnabled,
}) => {
  const { message, color, backgroundColor } = tickerTape ?? {};
  const colorRef = useRef(null);
  const bgColorRef = useRef(null);
  const [currentColor, setCurrentColor] = useState(color ?? '#000000');
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [currentBackgroundColor, setCurrentBackgroundColor] = useState(
    backgroundColor ?? '#FFFFFF',
  );
  const [
    displayBackgroundColorPicker,
    setDisplayBackgroundColorPicker,
  ] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (!isFirstRender && visible) {
      onUpdateTickerTape({
        message: tickerTapeEnabled ? message : null,
        color: tickerTapeEnabled ? currentColor : null,
        background_color: tickerTapeEnabled ? currentBackgroundColor : null,
      });
    }
  }, [currentColor, currentBackgroundColor]);

  useEffect(() => {
    if (!tickerTapeEnabled && !isFirstRender) {
      onUpdateTickerTape({
        message: null,
        color: null,
        background_color: null,
      });
    }
  }, [tickerTapeEnabled]);

  useEffect(() => {
    if (tickerTape?.message || tickerTapeEnabled) {
      setCurrentColor(color ?? backgroundColor);
      setCurrentBackgroundColor(backgroundColor ?? backgroundColor);
    } else {
      setCurrentColor('#000000');
      setCurrentBackgroundColor('#FFFFFF');
    }
  }, [tickerTape]);

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  useClickOutside(colorRef, () => {
    setDisplayColorPicker(false);
  });

  useClickOutside(bgColorRef, () => {
    setDisplayBackgroundColorPicker(false);
  });

  if (!visible) {
    return null;
  }

  return (
    <div className="w-100 d-flex flex-column-reverse flex-md-column">
      <div className="text-extra-bold text-dark text-sm d-flex w-100 justify-content-between align-items-center mt-2 mt-md-0 mb-md-2">
        {tickerTapeEnabled ? 'Remove' : 'Insert'} ticker tape banner
        <Switch
          uncheckedIcon={false}
          checkedIcon={false}
          height={20}
          width={40}
          onColor="#7ace21"
          offColor="#cdd6e4"
          className="pl-1"
          onChange={() => onToggleTickerTapeEnabled()}
          checked={tickerTapeEnabled}
        />
      </div>
      {tickerTapeEnabled && (
        <div>
          <Label id="text-color">Text color</Label>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div
            ref={colorRef}
            id="text-color"
            role="button"
            tabIndex={0}
            className="slideshow-designer-color-button mb-1"
            onClick={() => setDisplayColorPicker(true)}
          >
            <div
              className="slideshow-designer-color-control-addon"
              style={{ backgroundColor: currentColor }}
            />
            <span className="slideshow-designer-color-control-text">
              Change text color
            </span>
            {displayColorPicker ? (
              <div className="slideshow-designer-color-control-popover">
                <ChromePicker
                  color={currentColor}
                  onChange={value => setCurrentColor(value.hex)}
                  onChangeComplete={value => {
                    setCurrentColor(value.hex);
                  }}
                  width="100%"
                  disableAlpha
                />
              </div>
            ) : null}
          </div>
          <Label id="background-color">Background color</Label>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div
            ref={bgColorRef}
            id="background-color"
            role="button"
            tabIndex={0}
            className="slideshow-designer-color-button mb-1"
            onClick={() => setDisplayBackgroundColorPicker(true)}
          >
            <div
              className="slideshow-designer-color-control-addon"
              style={{ backgroundColor: currentBackgroundColor }}
            />
            <span className="slideshow-designer-color-control-text">
              Change background color
            </span>
            {displayBackgroundColorPicker ? (
              <div className="slideshow-designer-color-control-popover">
                <ChromePicker
                  color={currentBackgroundColor}
                  onChange={value => setCurrentBackgroundColor(value.hex)}
                  onChangeComplete={value => {
                    setCurrentBackgroundColor(value.hex);
                  }}
                  width="100%"
                  disableAlpha
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

SecondaryTickerTapeView.propTypes = propTypes;

export default SecondaryTickerTapeView;
